import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { impressumPageFooter } from './layout/footerConfigs'

export const ImpressumPage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { scrollProgress } = useOptimizedScroll()

  return (
    <>
      {/* Navbar is now global - no individual page navbars needed */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})`
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div
          className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
                y: [0, 30, 0],
              }
          }
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.3, 1],
                x: [0, -50, 0],
                y: [0, -30, 0],
              }
          }
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.15, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }
          }
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />
        <ContentSection prefersReducedMotion={prefersReducedMotion.current} />
      </div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative pt-48 pb-32" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          Impressum
        </motion.h1>
        <motion.p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}>
          Legal Information & Disclosure
        </motion.p>
      </div>
    </section>
  )
}


const ContentSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })

  return (
    <section ref={ref} className="relative py-16" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Company Details */}
        <motion.div
          className="mb-12"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 className="text-3xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
            Angaben gemäß § 5 TMG
          </h2>
          <div className="space-y-2" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, fontSize: '16px', lineHeight: '1.8' }}>
            <p><strong style={{ color: '#1A1A1A', fontWeight: 600 }}>Mernaki</strong></p>
            <p>Einzelunternehmen (Sole Proprietorship)</p>
            <p>Inhaber: Dallas ND</p>
            <p>Adresse: Ludwigstraße 8, 85354 Freising, Bavaria, Germany</p>
            <p>E-Mail: <a href="mailto:info@mernaki.de" style={{ color: '#4A90E2', textDecoration: 'none' }}>info@mernaki.de</a></p>
            <p>Web: <a href="https://mernaki.de" style={{ color: '#4A90E2', textDecoration: 'none' }}>https://mernaki.de</a></p>
          </div>
        </motion.div>

        {/* Responsible for Content */}
        <motion.div
          className="mb-12"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 className="text-3xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
            Verantwortlich für den Inhalt
          </h2>
          <div className="space-y-2" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, fontSize: '16px', lineHeight: '1.8' }}>
            <p>Verantwortlich für den Inhalt gemäß § 55 Abs. 2 RStV:</p>
            <p><strong style={{ color: '#1A1A1A', fontWeight: 600 }}>Dallas ND</strong></p>
            <p>Ludwigstraße 8, 85354 Freising, Bavaria, Germany</p>
          </div>
        </motion.div>

        {/* Liability Disclaimer */}
        <motion.div
          className="mb-12"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 className="text-3xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
            Haftungsausschluss
          </h2>
          <div className="space-y-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, fontSize: '16px', lineHeight: '1.8' }}>
            <p>
              Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links.
              Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.
            </p>
          </div>
        </motion.div>


        {/* Copyright */}
        <motion.div
          className="mb-12"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 className="text-3xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
            Urheberrecht
          </h2>
          <div className="space-y-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, fontSize: '16px', lineHeight: '1.8' }}>
            <p>
              Alle Inhalte (Texte, Grafiken, Designs, Logos, Videos) dieser Website unterliegen dem Urheberrecht.
            </p>
            <p>
              Eine Vervielfältigung oder Verwendung ist nur mit ausdrücklicher Genehmigung von Mernaki gestattet.
            </p>
          </div>
        </motion.div>

        {/* EU Dispute Resolution */}
        <motion.div
          className="mb-12"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 className="text-3xl mb-6" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
            EU-Streitschlichtung
          </h2>
          <div className="space-y-4" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, fontSize: '16px', lineHeight: '1.8' }}>
            <p>
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" style={{ color: '#4A90E2', textDecoration: 'none', marginLeft: '4px' }}>
                https://ec.europa.eu/consumers/odr
              </a>
            </p>
            <p>
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </div>
        </motion.div>

        {/* Brand Notice */}
        <motion.div
          className="p-6 rounded-2xl"
          style={{
            backgroundColor: 'rgba(74, 144, 226, 0.05)',
            border: '1px solid rgba(74, 144, 226, 0.2)'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <p className="text-sm text-center" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }}>
            Mernaki is a registered brand of Dallas ND.
          </p>
        </motion.div>

      </div>
    </section>
  )
}


const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  if (isMobile) {
    return <PageMobileFooter {...impressumPageFooter} />
  }

  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', marginTop: '-80px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Footer Header */}
        <motion.div
          className="text-center mb-12 pb-8 border-b"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h3
            className="text-2xl mb-2"
            style={{
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              letterSpacing: '-0.02em'
            }}
          >
            Impressum
          </h3>
          <p
            className="text-sm"
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 400
            }}
          >
            Legal information & disclosure
          </p>
        </motion.div>

        <div className="py-16">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                  alt="Mernaki Logo"
                  className="object-contain"
                  style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                  loading="lazy"
                />
                <h3
                  className="text-lg"
                  style={{
                    color: '#FFFFFF',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                    fontWeight: 400
                  }}
                >
                  Mernaki
                </h3>
              </div>
              <p
                className="text-sm leading-relaxed"
                style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                Empowering businesses with modern recruitment tools.
              </p>
            </div>

            {/* Column 2 - Company */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Company
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/about"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/partnerships"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Partnerships
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/team"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Team
                  </Link>
                </li>
              </ul>
            </div>


            {/* Column 3 - Support */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Support
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/help"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/report-issue"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Report an Issue
                  </Link>
                </li>
                <li>
                  <Link
                    to="/status"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Status Page
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 4 - Legal */}
            <div>
              <h3
                className="text-sm mb-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Legal
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cookies"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/impressum"
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    Impressum
                  </Link>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t py-6" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <p className="text-xs mb-2" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
              © {new Date().getFullYear()} Mernaki. Alle Rechte vorbehalten.
            </p>
            <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 300 }}>
              Mernaki is a registered brand of Dallas ND.
            </p>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}
